export type AdvancedMaterialCategory = 'metals';

export interface AdvancedMaterialPreset {
    id: string;
    parallaxId: string;
    name: string;
    category: AdvancedMaterialCategory;
    colorUrl: string;
    metalnessUrl?: string;
    normalUrl?: string;
    roughnessUrl: string;
    textureScale: number;
    normalStrength: number;
    roughnessScale?: number;
    environmentIntensity?: number;
    opacityUrl?: string;
    alphaCutoff?: number;
    doubleSided?: boolean;
    displacementUrl?: string;
    parallaxHeightMeters?: number;
    repetitionWarpStrength?: number;
    repetitionWarpFrequency?: number;
}

export type AdvancedMaterialParallaxQuality = NonNullable<IAdvancedMaterialTuning['parallaxQuality']>;

export const ADVANCED_MATERIAL_PARALLAX_QUALITIES: AdvancedMaterialParallaxQuality[] = [
    'low',
    'medium',
    'high',
];

const DEFAULT_PARALLAX_QUALITY: AdvancedMaterialParallaxQuality = 'medium';

const materialAssetUrls = import.meta.glob<string>('../assets/Metal061B/*.jpg', {
    eager: true,
    query: '?url',
    import: 'default',
});

const getMaterialAssetUrl = (materialName: string, mapName: string): string => {
    const path = `../assets/${materialName}/${materialName}_1K-JPG_${mapName}.jpg`;
    const url = materialAssetUrls[path];
    if (!url) throw new Error(`Missing advanced material asset: ${path}`);
    return url;
};

interface MaterialDefinition {
    id: string;
    parallaxId: string;
    name: string;
    category: AdvancedMaterialCategory;
    isNonMetal?: boolean;
    hasOpacity?: boolean;
    hasDisplacement?: boolean;
    normalStrength?: number;
    roughnessScale?: number;
    environmentIntensity?: number;
    parallaxHeightMeters?: number;
    repetitionWarpStrength?: number;
    repetitionWarpFrequency?: number;
}

const createPreset = ({
    id,
    parallaxId,
    name,
    category,
    isNonMetal,
    hasOpacity,
    hasDisplacement,
    normalStrength,
    roughnessScale,
    environmentIntensity,
    parallaxHeightMeters,
    repetitionWarpStrength,
    repetitionWarpFrequency,
}: MaterialDefinition): AdvancedMaterialPreset => ({
    id,
    parallaxId,
    name,
    category,
    colorUrl: getMaterialAssetUrl(name, 'Color'),
    metalnessUrl: isNonMetal ? undefined : getMaterialAssetUrl(name, 'Metalness'),
    normalUrl: getMaterialAssetUrl(name, 'NormalGL'),
    roughnessUrl: getMaterialAssetUrl(name, 'Roughness'),
    textureScale: 0.5,
    normalStrength: normalStrength ?? 1,
    roughnessScale,
    environmentIntensity,
    opacityUrl: hasOpacity ? getMaterialAssetUrl(name, 'Opacity') : undefined,
    alphaCutoff: hasOpacity ? 0.5 : undefined,
    doubleSided: hasOpacity || undefined,
    displacementUrl: hasDisplacement ? getMaterialAssetUrl(name, 'Displacement') : undefined,
    parallaxHeightMeters,
    repetitionWarpStrength,
    repetitionWarpFrequency,
});

export const ADVANCED_MATERIAL_TUNING_RANGES = {
    textureScale: { min: 0.1, max: 10, step: 0.05 },
    normalStrength: { min: 0, max: 4, step: 0.05 },
    roughnessScale: { min: 0.25, max: 2, step: 0.05 },
    metalnessStrength: { min: 0, max: 1, step: 0.05 },
    environmentIntensity: { min: 0, max: 1.5, step: 0.05 },
    triplanarBlendSharpness: { min: 1, max: 16, step: 0.25 },
    parallaxHeightMeters: { min: 0, max: 0.1, step: 0.001 },
    opacityCutoff: { min: 0, max: 1, step: 0.01 },
    repetitionWarpStrength: { min: 0, max: 1.5, step: 0.025 },
    repetitionWarpFrequency: { min: 0.05, max: 1, step: 0.025 },
} as const;

const clampTuningValue = (
    value: number | undefined,
    fallback: number,
    range: { min: number; max: number }
): number => {
    const next = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
    return Math.min(range.max, Math.max(range.min, next));
};

const normalizeParallaxQuality = (
    value: IAdvancedMaterialTuning['parallaxQuality']
): AdvancedMaterialParallaxQuality =>
    ADVANCED_MATERIAL_PARALLAX_QUALITIES.includes(value as AdvancedMaterialParallaxQuality)
        ? value as AdvancedMaterialParallaxQuality
        : DEFAULT_PARALLAX_QUALITY;

export const getAdvancedMaterialTuning = (
    preset: AdvancedMaterialPreset,
    tuning?: IAdvancedMaterialTuning | null
): IAdvancedMaterialTuning => ({
    textureScale: clampTuningValue(
        tuning?.textureScale,
        preset.textureScale,
        ADVANCED_MATERIAL_TUNING_RANGES.textureScale
    ),
    normalStrength: clampTuningValue(
        tuning?.normalStrength,
        preset.normalStrength,
        ADVANCED_MATERIAL_TUNING_RANGES.normalStrength
    ),
    roughnessScale: clampTuningValue(
        tuning?.roughnessScale,
        preset.roughnessScale ?? 1,
        ADVANCED_MATERIAL_TUNING_RANGES.roughnessScale
    ),
    metalnessStrength: preset.metalnessUrl
        ? clampTuningValue(
            tuning?.metalnessStrength,
            1,
            ADVANCED_MATERIAL_TUNING_RANGES.metalnessStrength
        )
        : undefined,
    environmentIntensity: clampTuningValue(
        tuning?.environmentIntensity,
        preset.environmentIntensity ?? 0.4,
        ADVANCED_MATERIAL_TUNING_RANGES.environmentIntensity
    ),
    triplanarBlendSharpness: clampTuningValue(
        tuning?.triplanarBlendSharpness,
        4,
        ADVANCED_MATERIAL_TUNING_RANGES.triplanarBlendSharpness
    ),
    parallaxHeightMeters: preset.displacementUrl
        ? clampTuningValue(
            tuning?.parallaxHeightMeters,
            preset.parallaxHeightMeters ?? 0,
            ADVANCED_MATERIAL_TUNING_RANGES.parallaxHeightMeters
        )
        : undefined,
    parallaxQuality: preset.displacementUrl
        ? normalizeParallaxQuality(tuning?.parallaxQuality)
        : undefined,
    opacityCutoff: preset.opacityUrl
        ? clampTuningValue(
            tuning?.opacityCutoff,
            preset.alphaCutoff ?? 0.5,
            ADVANCED_MATERIAL_TUNING_RANGES.opacityCutoff
        )
        : undefined,
    repetitionWarpStrength: preset.repetitionWarpStrength != null
        ? clampTuningValue(
            tuning?.repetitionWarpStrength,
            preset.repetitionWarpStrength,
            ADVANCED_MATERIAL_TUNING_RANGES.repetitionWarpStrength
        )
        : undefined,
    repetitionWarpFrequency: preset.repetitionWarpStrength != null
        ? clampTuningValue(
            tuning?.repetitionWarpFrequency,
            preset.repetitionWarpFrequency ?? 0.35,
            ADVANCED_MATERIAL_TUNING_RANGES.repetitionWarpFrequency
        )
        : undefined,
});

export const ADVANCED_MATERIAL_IDS = {
    Metal061B: '838ff69f-61a7-4c9d-8492-c31a3c2cf93e',
} as const;

// Retained for compatibility with Knowledge Views created by the prototype UI.
// Metal061B has no displacement map, so both IDs render the same preset.
export const ADVANCED_MATERIAL_PARALLAX_IDS = {
    Metal061B: '61f0921f-179c-40e1-9318-071b8dc610c8',
} as const;

export const ADVANCED_MATERIAL_PRESETS: AdvancedMaterialPreset[] = [
    createPreset({
        id: ADVANCED_MATERIAL_IDS.Metal061B,
        parallaxId: ADVANCED_MATERIAL_PARALLAX_IDS.Metal061B,
        name: 'Metal061B',
        category: 'metals',
        normalStrength: 2.75,
        environmentIntensity: 0.7,
        repetitionWarpStrength: 0.5,
        repetitionWarpFrequency: 0.5,
    }),
];

export const getAdvancedMaterialPreset = (id?: string | null): AdvancedMaterialPreset | null => {
    const normalizedID = id?.toLocaleLowerCase();
    if (!normalizedID) return null;
    return ADVANCED_MATERIAL_PRESETS.find(preset =>
        preset.id.toLocaleLowerCase() === normalizedID || preset.parallaxId.toLocaleLowerCase() === normalizedID
    ) ?? null;
};

export const isAdvancedMaterialParallaxEnabled = (id?: string | null): boolean =>
    ADVANCED_MATERIAL_PRESETS.some(preset => preset.parallaxId === id && !!preset.displacementUrl);

export const getAdvancedMaterialSelectionId = (preset: AdvancedMaterialPreset, parallaxEnabled: boolean): string =>
    parallaxEnabled && preset.displacementUrl ? preset.parallaxId : preset.id;
